import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Input,
  Label,
  StyledList,
  StyledListItem,
} from './StyledComponents';
import {
  createBackup,
  deleteBackup,
  listBackups,
  restoreBackup,
} from '../firebase/firebaseUtils';

const StatusMessage = ({ status }) => {
  if (!status?.message) {
    return null;
  }
  const color = status.kind === 'error' ? '#c26166' : '#4caf50';
  return (
    <p style={{ color, margin: '5px 0' }}>
      {status.message}
    </p>
  );
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) {
    return 'Unknown date';
  }
  try {
    return new Date(timestamp).toLocaleString();
  } catch (error) {
    console.error('Failed to format timestamp', error);
    return `${timestamp}`;
  }
};

const BackupManager = ({ userId }) => {
  const [backupName, setBackupName] = useState('');
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasLoadedBackups, setHasLoadedBackups] = useState(false);
  const [status, setStatus] = useState(null);

  const hasBackups = backups.length > 0;

  const loadBackups = useCallback(async () => {
    if (!userId) {
      setBackups([]);
      setHasLoadedBackups(true);
      return;
    }
    setRefreshing(true);
    try {
      const result = await listBackups(userId);
      setBackups(result);
      setStatus(null);
    } catch (error) {
      console.error('Failed to load backups', error);
      setStatus({ kind: 'error', message: 'Failed to load backups. Please try again.' });
    } finally {
      setHasLoadedBackups(true);
      setRefreshing(false);
    }
  }, [userId]);

  useEffect(() => {
    setHasLoadedBackups(false);
    loadBackups();
  }, [loadBackups]);

  const handleCreateBackup = async () => {
    if (!userId) {
      setStatus({ kind: 'error', message: 'Missing user ID. Please re-authenticate.' });
      return;
    }
    setLoading(true);
    try {
      const response = await createBackup(userId, { name: backupName });
      setStatus({ kind: 'success', message: `Backup "${response.name}" created.` });
      setBackupName('');
      await loadBackups();
    } catch (error) {
      console.error('Failed to create backup', error);
      setStatus({ kind: 'error', message: 'Failed to create backup. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreBackup = async (backupId) => {
    if (!userId) {
      setStatus({ kind: 'error', message: 'Missing user ID. Please re-authenticate.' });
      return;
    }
    const target = backups.find((backup) => backup.id === backupId);
    setLoading(true);
    try {
      await restoreBackup(userId, backupId);
      setStatus({
        kind: 'success',
        message: `Restored backup "${target?.name || backupId}". Reload the app to ensure everything is synced.`,
      });
      await loadBackups();
    } catch (error) {
      console.error('Failed to restore backup', error);
      setStatus({
        kind: 'error',
        message: 'Failed to restore backup. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!userId) {
      setStatus({ kind: 'error', message: 'Missing user ID. Please re-authenticate.' });
      return;
    }
    const target = backups.find((backup) => backup.id === backupId);
    setLoading(true);
    try {
      await deleteBackup(userId, backupId);
      setStatus({
        kind: 'success',
        message: `Deleted backup "${target?.name || backupId}".`,
      });
      await loadBackups();
    } catch (error) {
      console.error('Failed to delete backup', error);
      setStatus({
        kind: 'error',
        message: 'Failed to delete backup. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const automaticSummary = useMemo(() => {
    if (!hasBackups) {
      return null;
    }
    const latestAutomatic = backups.find((backup) => backup.type === 'automatic');
    if (!latestAutomatic) {
      return null;
    }
    return `Last automatic backup: ${formatTimestamp(latestAutomatic.createdAt)}.`;
  }, [backups, hasBackups]);

  return (
    <div style={{ marginTop: '20px' }}>
      <Label>Backup Name</Label>
      <Input
        type="text"
        value={backupName}
        placeholder="e.g. Before Tax Filing"
        onChange={(event) => setBackupName(event.target.value)}
      />
      <div>
        <Button
          type="button"
          onClick={handleCreateBackup}
          disabled={loading}
        >
          Create Backup
        </Button>
        <Button
          type="button"
          onClick={loadBackups}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh List'}
        </Button>
      </div>
      <StatusMessage status={status} />
      {(!hasLoadedBackups || (refreshing && !hasBackups)) && (
        <p style={{ marginTop: '10px' }}>Loading backups...</p>
      )}
      {automaticSummary && (
        <p style={{ margin: '10px 0' }}>{automaticSummary}</p>
      )}
      {hasBackups ? (
        <StyledList>
          {backups.map((backup) => (
            <StyledListItem key={backup.id} $clickable={false}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <strong>{backup.name || backup.id}</strong>
                <span>Created: {formatTimestamp(backup.createdAt)}</span>
                <span>Type: {backup.type === 'automatic' ? 'Automatic' : 'Manual'}</span>
              </div>
              <span>
                <Button
                  type="button"
                  onClick={() => handleRestoreBackup(backup.id)}
                  disabled={loading}
                >
                  Restore
                </Button>
                <Button
                  type="button"
                  onClick={() => handleDeleteBackup(backup.id)}
                  disabled={loading}
                >
                  Delete
                </Button>
              </span>
            </StyledListItem>
          ))}
        </StyledList>
      ) : hasLoadedBackups && !refreshing ? (
        <p style={{ marginTop: '10px' }}>No backups yet. Create one to get started.</p>
      ) : null}
    </div>
  );
};

export default BackupManager;
